import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Search, Table, Eye, ArrowRight, Database, AlertTriangle } from 'lucide-react';
import { useAvailableTables, type TableInfo, type ObjectType } from '../hooks/useAvailableTables';
import type { DatabaseEngineId } from '../../../../core/engines';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../../components/ui/tooltip';
import { Button } from '../../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectTrigger,
} from '../../../../components/ui/select';
import { useDatabaseList } from '../../../database-objects/hooks/useDatabaseList';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { getDatabaseFromConnectionId } from '../../../connections/utils/getDatabaseFromConnectionId';
import { FallbackSelectContent } from '../../../connection-diagnostics/components/FallbackSelectContent';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
export type ExplorerSelectedTable = {
  name: string;
  schema: string;
  database: string;
};

type TableSelectorProps = {
  connectionId: string;
  database: string;
  engineId?: DatabaseEngineId;
  onSelectTable: (table: TableInfo) => void;
  className?: string;
  /** Table currently selected in the explorer (shown as quick-access option) */
  explorerSelectedTable?: ExplorerSelectedTable;
  /** Callback when database is changed via the dropdown */
  onDatabaseChange?: (database: string) => void;
};

type FilterType = 'all' | 'table' | 'view';

const MIN_TILE_WIDTH = 180;

export function TableSelector({
  connectionId,
  database,
  engineId = 'mssql',
  onSelectTable,
  className = '',
  explorerSelectedTable,
  onDatabaseChange,
}: TableSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filterType, setFilterType] = useState<FilterType>('all');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Database selector state
  const [isDatabaseDropdownOpen, setIsDatabaseDropdownOpen] = useState(false);

  // Get engine from connection store
  const connections = useConnectionStore((state) => state.connections);
  const activeConnections = useConnectionStore((state) => state.activeConnections);
  const resolvedEngine = connectionId
    ? connections.find((c) => c.id === connectionId)?.engine ||
      activeConnections.find((c) => c.id === connectionId)?.connection.engine ||
      engineId
    : engineId;

  // Shared per-connection listing — same hook, same stale-while-revalidate
  // contract as the Query Editor's status-bar switcher (CLAUDE.md: the two
  // grid surfaces must behave identically). The dropdown's open state gates it,
  // so exactly one real listing runs per open; react-query's own dedupe (not an
  // ad-hoc request-id) is what keeps a parent re-render from racing a second
  // fetch against the Slice 5 failure-injection queue.
  const {
    databases,
    state: databasesState,
    isInitialLoading: isDatabasesLoading,
  } = useDatabaseList({
    connectionId,
    engine: resolvedEngine,
    enabled: isDatabaseDropdownOpen && !!onDatabaseChange,
  });

  // `onDatabaseChange` is kept in a ref for the seeding effect below: parents
  // often pass an inline arrow, so its reference changes on every parent
  // re-render and must not re-run the effect.
  const onDatabaseChangeRef = useRef(onDatabaseChange);
  useEffect(() => {
    onDatabaseChangeRef.current = onDatabaseChange;
  });

  // Seed the database with the connection's saved default when the caller
  // opens this picker without one (mirrors the prefill applied to the other
  // connection→database pickers). `connectionId` here is fixed by the caller
  // (no in-place connection switcher inside this component), so this only
  // needs to run once a connection is present and no database is selected yet.
  useEffect(() => {
    if (!connectionId || database || !onDatabaseChangeRef.current) return;
    const seeded = getDatabaseFromConnectionId(connectionId, connections, activeConnections);
    if (seeded) onDatabaseChangeRef.current(seeded);
  }, [connectionId, database, connections, activeConnections]);

  // Calculate columns per row dynamically based on grid width
  const getColumnsPerRow = useCallback(() => {
    if (!gridRef.current) return 3;
    const gridWidth = gridRef.current.clientWidth;
    const gap = 8; // gap-2 = 0.5rem = 8px
    return Math.max(1, Math.floor((gridWidth + gap) / (MIN_TILE_WIDTH + gap)));
  }, []);

  const { track } = useAnalytics();

  const { data: allObjects = [], isLoading, error, refetch } = useAvailableTables({
    connectionId,
    database,
    engineId,
    enabled: !!connectionId && !!database,
  });

  // F06 — object types that failed to list on the last fetch (e.g. `getViews`
  // errored while `getTables` succeeded). `allObjects` still only carries
  // whatever DID load; this drives the loud, retryable banner below instead
  // of letting a partial list quietly look complete.
  const failedObjectTypes: ObjectType[] = useMemo(
    () => allObjects.partialListing?.failedObjectTypes ?? [],
    [allObjects]
  );

  const handleRetryListing = useCallback(() => {
    track('feature:table_picker_retry', {
      target: failedObjectTypes.length === 2 ? 'both' : failedObjectTypes[0] ?? 'unknown',
    });
    refetch();
  }, [track, refetch, failedObjectTypes]);

  // Filter objects based on search query and type filter
  const filteredObjects = useMemo(() => {
    let result = allObjects;

    // Apply type filter
    if (filterType !== 'all') {
      result = result.filter((obj) => obj.objectType === filterType);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (obj) =>
          obj.name.toLowerCase().includes(query) ||
          obj.schema.toLowerCase().includes(query)
      );
    }

    return result;
  }, [allObjects, searchQuery, filterType]);

  // Count by type for the filter buttons
  const counts = useMemo(() => {
    const tables = allObjects.filter((o) => o.objectType === 'table').length;
    const views = allObjects.filter((o) => o.objectType === 'view').length;
    return { all: allObjects.length, tables, views };
  }, [allObjects]);

  // F06 (D10) — completeness strip copy. Filter-scoped: the "of M" total is
  // the count *within the active type tab* (matching the tab badges), not the
  // whole-database count, because the grid itself is filter-scoped.
  const completenessSummary = useMemo(() => {
    if (isLoading) return 'Loading…';
    const scopedTotal = filterType === 'all' ? counts.all : filterType === 'table' ? counts.tables : counts.views;
    const isSearching = searchQuery.trim().length > 0;
    if (!isSearching) {
      // Singularize: "Showing all 1 table" / "1 view", not "1 tables". The
      // "all" tab's compound noun only makes sense for >1 total — with
      // exactly one object we know which kind it is from the type counts.
      let noun: string;
      if (filterType === 'table') {
        noun = scopedTotal === 1 ? 'table' : 'tables';
      } else if (filterType === 'view') {
        noun = scopedTotal === 1 ? 'view' : 'views';
      } else {
        noun = scopedTotal === 1 ? (counts.tables === 1 ? 'table' : 'view') : 'tables and views';
      }
      return `Showing all ${scopedTotal} ${noun}`;
    }
    const noun = filterType === 'all' ? 'objects' : filterType === 'table' ? 'tables' : 'views';
    const matchWord = filteredObjects.length === 1 ? 'matches' : 'match';
    return `${filteredObjects.length} of ${scopedTotal} ${noun} ${matchWord}`;
  }, [isLoading, filterType, counts, searchQuery, filteredObjects.length]);

  // Reset selection when filter changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery, filterType]);

  // Focus search input on mount
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // Handle keyboard navigation - dynamic columns based on grid width
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (filteredObjects.length === 0) return;

      const totalItems = filteredObjects.length;
      const columnsPerRow = getColumnsPerRow();

      switch (e.key) {
        case 'ArrowRight': {
          e.preventDefault();
          const nextIndex = selectedIndex + 1;
          if (nextIndex < totalItems) {
            setSelectedIndex(nextIndex);
          }
          break;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          const prevIndex = selectedIndex - 1;
          if (prevIndex >= 0) {
            setSelectedIndex(prevIndex);
          }
          break;
        }
        case 'ArrowDown': {
          e.preventDefault();
          const nextIndex = selectedIndex + columnsPerRow;
          if (nextIndex < totalItems) {
            setSelectedIndex(nextIndex);
          } else if (selectedIndex < totalItems - 1) {
            // Move to last item if we can't go down a full row
            setSelectedIndex(totalItems - 1);
          }
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const prevIndex = selectedIndex - columnsPerRow;
          if (prevIndex >= 0) {
            setSelectedIndex(prevIndex);
          } else if (selectedIndex > 0) {
            // Move to first item if we can't go up a full row
            setSelectedIndex(0);
          }
          break;
        }
        case 'Enter': {
          e.preventDefault();
          if (filteredObjects[selectedIndex]) {
            onSelectTable(filteredObjects[selectedIndex]);
          }
          break;
        }
      }
    },
    [filteredObjects, selectedIndex, onSelectTable, getColumnsPerRow]
  );

  // Scroll selected item into view
  useEffect(() => {
    if (!containerRef.current) return;
    const selectedCard = containerRef.current.querySelector('[data-selected="true"]');
    if (selectedCard) {
      selectedCard.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  // Handle object click
  const handleObjectClick = useCallback(
    (obj: TableInfo, index: number) => {
      setSelectedIndex(index);
      onSelectTable(obj);
    },
    [onSelectTable]
  );

  if (!connectionId || !database) {
    return (
      <div className={`flex flex-col items-center justify-center h-full ${className}`}>
        <p className="text-muted-foreground">Select a database to browse tables</p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div
      className={`flex flex-col h-full overflow-hidden ${className}`}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {/* Sticky Header Section */}
      <div className="flex-shrink-0 p-4 pb-0">
        {/* Quick Access - Show explorer-selected table at top */}
        {explorerSelectedTable && explorerSelectedTable.database.toLowerCase() === database.toLowerCase() && (
          <div className="mb-4 max-w-md mx-auto w-full">
            <div className="text-xs text-muted-foreground mb-2 text-center">Quick Access</div>
            <Button
              variant="outline"
              className="w-full justify-start gap-2 h-auto py-2 px-3 border-primary/50 bg-primary/5 hover:bg-primary/10"
              onClick={() => {
                // Find the matching table in allObjects to get full TableInfo
                const matchingTable = allObjects.find(
                  (obj) =>
                    obj.name === explorerSelectedTable.name &&
                    obj.schema === explorerSelectedTable.schema
                );
                if (matchingTable) {
                  onSelectTable(matchingTable);
                } else {
                  // If not found in loaded tables, create a basic TableInfo
                  // Use the current database prop (from dropdown), not explorerSelectedTable's database
                  onSelectTable({
                    name: explorerSelectedTable.name,
                    schema: explorerSelectedTable.schema,
                    database: database,
                    objectType: 'table',
                  });
                }
              }}
              data-testid="quick-access-table-button"
            >
              <ArrowRight className="h-4 w-4 text-primary" />
              <div className="flex flex-col items-start">
                <span className="text-sm font-medium">{explorerSelectedTable.name}</span>
                <span className="text-xs text-muted-foreground">{explorerSelectedTable.schema}</span>
              </div>
            </Button>
          </div>
        )}

        {/* Header with Database Selector */}
        <div className="text-center mb-4">
          <h2 className="text-lg font-semibold mb-2">Select a Table or View</h2>
          {onDatabaseChange ? (
            <Select
              value={database}
              onValueChange={onDatabaseChange}
              onOpenChange={setIsDatabaseDropdownOpen}
              open={isDatabaseDropdownOpen}
            >
              <SelectTrigger
                data-testid="table-selector-database-dropdown"
                className="h-9 w-auto min-w-[200px] max-w-[400px] mx-auto px-3 gap-2 bg-muted/50 border-border hover:bg-muted transition-colors"
              >
                <Database className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="font-medium truncate">{database || 'Select database'}</span>
              </SelectTrigger>
              <SelectContent
                data-testid="table-selector-database-content"
                className="max-h-60 overflow-auto min-w-[200px]"
                position="popper"
                align="center"
              >
                {isDatabasesLoading ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground flex items-center gap-2">
                    <RandomDotmSpinner size={16} />
                    Loading databases...
                  </div>
                ) : (
                  <FallbackSelectContent
                    connectionId={connectionId}
                    state={databasesState}
                    databases={databases}
                    isLoading={isDatabasesLoading}
                    selectedDatabase={database}
                    testIdPrefix="table-selector"
                    beforeOpenDetails={() => setIsDatabaseDropdownOpen(false)}
                  />
                )}
              </SelectContent>
            </Select>
          ) : (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/50 text-sm">
              <Database className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{database}</span>
            </div>
          )}
        </div>

        {/* Search and Filter Row */}
        <div className="flex items-center gap-3 mb-4 max-w-2xl mx-auto w-full">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="w-full pl-9 pr-16 py-1.5 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="table-search-input"
          />
        </div>

        {/* Type Filter */}
        <div className="flex rounded-md border border-input bg-background" data-testid="type-filter">
          <FilterButton
            label="All"
            count={counts.all}
            isActive={filterType === 'all'}
            onClick={() => setFilterType('all')}
          />
          <FilterButton
            label="Tables"
            count={counts.tables}
            isActive={filterType === 'table'}
            onClick={() => setFilterType('table')}
          />
          <FilterButton
            label="Views"
            count={counts.views}
            isActive={filterType === 'view'}
            onClick={() => setFilterType('view')}
          />
        </div>
        </div>
      </div>

      {/* Scrollable Content Section */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4" data-testid="table-selector-scroll-region">
      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <RandomDotmSpinner size={20} className="text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="text-center py-8">
          <p className="text-sm text-destructive">
            Failed to load: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && filteredObjects.length === 0 && (
        <div className="text-center py-8">
          <p className="text-sm text-muted-foreground">
            {searchQuery
              ? `No ${filterType === 'all' ? 'objects' : filterType + 's'} match "${searchQuery}"`
              : `No ${filterType === 'all' ? 'tables or views' : filterType + 's'} found`}
          </p>
        </div>
      )}

      {/* Object Grid */}
      {!isLoading && !error && filteredObjects.length > 0 && (
        <div
          ref={containerRef}
          data-testid="table-selector-grid"
        >
          <div
            ref={gridRef}
            className="grid gap-2"
            style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${MIN_TILE_WIDTH}px, 1fr))` }}
          >
            {filteredObjects.map((obj, index) => (
              <ObjectCard
                key={`${obj.schema}.${obj.name}.${obj.objectType}`}
                object={obj}
                isSelected={index === selectedIndex}
                onClick={() => handleObjectClick(obj, index)}
              />
            ))}
          </div>
        </div>
      )}
      </div>

      {/* Persistent footer (F06/D10) — pinned OUTSIDE the scroll region so
          the completeness strip and keyboard hint are always visible, not
          just when a short/filtered list happens to fit above the fold. */}
      {!error && (
        <div className="flex-shrink-0 border-t border-border px-4 py-2" data-testid="table-selector-footer">
          {failedObjectTypes.length > 0 && !isLoading && (
            <div
              className="flex items-center justify-between gap-2 mb-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-700 dark:text-amber-400"
              data-testid="table-selector-listing-error"
            >
              <span className="flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {failedObjectTypes.length === 2
                  ? 'Tables and views'
                  : failedObjectTypes[0] === 'table'
                    ? 'Tables'
                    : 'Views'}{' '}
                could not be listed.
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-6 shrink-0 px-2 text-xs"
                onClick={handleRetryListing}
                data-testid="table-selector-listing-retry"
              >
                Retry
              </Button>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
            <span data-testid="table-selector-count-summary">{completenessSummary}</span>
            {!isLoading && filteredObjects.length > 0 && (
              <span className="inline-flex items-center gap-1.5 shrink-0">
                <kbd className="font-mono not-italic">↑↓←→</kbd>
                <span>navigate</span>
                <span aria-hidden>·</span>
                <kbd className="font-mono not-italic">Enter</kbd>
                <span>select</span>
              </span>
            )}
          </div>
        </div>
      )}
      </div>
    </TooltipProvider>
  );
}

// Filter Button Component
type FilterButtonProps = {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
};

function FilterButton({ label, count, isActive, onClick }: FilterButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        px-2.5 py-1 text-xs font-medium transition-colors first:rounded-l-md last:rounded-r-md
        ${isActive
          ? 'bg-primary text-primary-foreground'
          : 'hover:bg-accent text-muted-foreground'
        }
      `}
    >
      {label} <span className="opacity-70">({count})</span>
    </button>
  );
}

// Object Card Component
type ObjectCardProps = {
  object: TableInfo;
  isSelected: boolean;
  onClick: () => void;
};

function ObjectCard({ object, isSelected, onClick }: ObjectCardProps) {
  const Icon = object.objectType === 'view' ? Eye : Table;
  const fullName = `${object.schema}.${object.name}`;
  const needsTooltip = object.name.length > 18;

  const button = (
    <button
      type="button"
      onClick={onClick}
      data-selected={isSelected}
      data-testid={`table-card-${object.schema}-${object.name}`}
      className={`
        flex items-center gap-2 px-2.5 py-1.5 rounded-md border transition-[background-color,border-color,box-shadow] text-left w-full
        hover:bg-accent hover:border-accent-foreground/20
        focus:outline-none focus:ring-2 focus:ring-ring
        ${isSelected ? 'bg-accent border-primary ring-1 ring-primary/50' : 'bg-card border-border'}
      `}
    >
      <Icon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <span className="text-sm font-medium truncate block">{object.name}</span>
        <span className="text-[10px] text-muted-foreground truncate block">{object.schema}</span>
      </div>
    </button>
  );

  if (!needsTooltip) {
    return button;
  }

  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="bottom">
        <p>{fullName}</p>
      </TooltipContent>
    </Tooltip>
  );
}
